(function () {
  if (window.ShopifyWishlist) {
    return;
  }

  const STORAGE_KEY = 'shopify_wishlist';
  const SYNC_DELAY = 500;

  class WishlistManager {
    constructor() {
      this.items = [];
      this.subscribers = new Set();
      this.readyCallbacks = [];
      this.isReady = false;
      this.syncTimer = null;
      this.lastSyncedPayload = null;

      this.customerPayload = this.parseCustomerPayload();
      this.hiddenForm = document.getElementById('wishlist-sync-form');
      this.hiddenInput = this.hiddenForm
        ? this.hiddenForm.querySelector('input[name="customer[metafields][custom][wishlist]"]')
        : null;
      if (this.hiddenForm) {
        const accountUrl = (window.routes && window.routes.account_url) || '/account';
        if (this.hiddenForm.getAttribute('action') !== accountUrl) {
          this.hiddenForm.setAttribute('action', accountUrl);
        }
      }

      this.restoreState();
    }

    parseCustomerPayload() {
      const script = document.getElementById('customer-wishlist-data');
      if (!script) {
        return null;
      }

      try {
        const data = JSON.parse(script.textContent.trim() || 'null');
        return data && typeof data === 'object' ? data : null;
      } catch (error) {
        console.error('Wishlist: unable to parse customer payload', error);
        return null;
      }
    }

    restoreState() {
      const localItems = this.readLocal();
      const customerItems = this.readCustomerMetafield();

      if (customerItems.length) {
        this.items = this.mergeItems(customerItems, localItems);
      } else {
        this.items = [...localItems];
      }

      this.persistLocal();
      if (this.hasCustomer()) {
        this.queueSync();
      }

      this.isReady = true;
      this.flushReady();
      this.publish({ origin: 'wishlist-manager:init' });
    }

    hasCustomer() {
      return Boolean(this.customerPayload && this.customerPayload.id);
    }

    readCustomerMetafield() {
      if (!this.hasCustomer()) {
        return [];
      }

      const value = this.customerPayload?.metafields?.value;
      if (Array.isArray(value)) {
        return this.normalizeItems(value);
      }

      if (typeof value === 'string' && value.trim()) {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? this.normalizeItems(parsed) : [];
        } catch (error) {
          console.error('Wishlist: unable to parse metafield JSON', error);
        }
      }

      return [];
    }

    readLocal() {
      try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (!stored) {
          return [];
        }
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? this.normalizeItems(parsed) : [];
      } catch (error) {
        console.error('Wishlist: unable to read local storage', error);
        return [];
      }
    }

    persistLocal() {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (error) {
        console.error('Wishlist: unable to write local storage', error);
      }
    }

    mergeItems(primary, secondary) {
      const map = new Map();
      primary.forEach((item) => {
        if (item && item.id != null) {
          map.set(String(item.id), this.normalizeItem(item));
        }
      });
      secondary.forEach((item) => {
        const key = item && item.id != null ? String(item.id) : null;
        if (key && !map.has(key)) {
          map.set(key, this.normalizeItem(item));
        }
      });
      return Array.from(map.values()).filter(Boolean);
    }

    normalizeItems(items) {
      return items.map((item) => this.normalizeItem(item)).filter(Boolean);
    }

    normalizeItem(item) {
      if (!item || item.id == null) {
        return null;
      }

      const normalized = {
        id: String(item.id),
        handle: item.handle || null,
        title: item.title || null,
        url: item.url || null,
        price: Number.isFinite(Number(item.price)) ? Number(item.price) : 0,
        image: item.image || null,
        variantId: item.variantId != null ? String(item.variantId) : null,
        addedAt: item.addedAt || new Date().toISOString(),
        options: Array.isArray(item.options) ? item.options : [],
      };

      return normalized;
    }

    ready(callback) {
      if (typeof callback !== 'function') {
        return;
      }
      if (this.isReady) {
        callback(this.getItems());
        return;
      }
      this.readyCallbacks.push(callback);
    }

    flushReady() {
      const items = this.getItems();
      this.readyCallbacks.splice(0).forEach((callback) => {
        try {
          callback(items);
        } catch (error) {
          console.error('Wishlist: ready callback error', error);
        }
      });
    }

    subscribe(callback) {
      if (typeof callback !== 'function') {
        return () => {};
      }
      this.subscribers.add(callback);
      return () => {
        this.subscribers.delete(callback);
      };
    }

    publish(meta = {}) {
      const payload = this.getItems();
      this.subscribers.forEach((callback) => {
        try {
          callback(payload, meta);
        } catch (error) {
          console.error('Wishlist: subscriber error', error);
        }
      });

      document.dispatchEvent(
        new CustomEvent('wishlist:updated', {
          detail: { wishlist: payload, origin: meta.origin || 'wishlist-manager' },
        })
      );

      this.updateBadgeDisplays(payload.length);
    }

    getItems() {
      return this.items.map((item) => ({ ...item }));
    }

    contains(id) {
      return this.items.some((item) => String(item.id) === String(id));
    }

    addItem(item, meta = {}) {
      const normalized = this.normalizeItem(item);
      if (!normalized) {
        return;
      }

      if (this.contains(normalized.id)) {
        return;
      }

      this.items = [...this.items, normalized];
      this.persistLocal();
      this.queueSync();
      this.publish(meta);
    }

    removeItem(id, meta = {}) {
      const key = String(id);
      const filtered = this.items.filter((item) => String(item.id) !== key);
      if (filtered.length === this.items.length) {
        return;
      }

      this.items = filtered;
      this.persistLocal();
      this.queueSync();
      this.publish(meta);
    }

    setItems(items, meta = {}) {
      const normalized = this.normalizeItems(Array.isArray(items) ? items : []);
      this.items = normalized;
      this.persistLocal();
      this.queueSync();
      this.publish(meta);
    }

    updateBadgeDisplays(count) {
      document.querySelectorAll('.wishlist-count').forEach((element) => {
        element.textContent = count;
      });

      document.querySelectorAll('.wishlist-count-bubble').forEach((bubble) => {
        const countElement = bubble.querySelector('.wishlist-count') || bubble;
        countElement.textContent = count;

        const srText = bubble.querySelector('.visually-hidden');
        if (srText) {
          srText.textContent = count === 1 ? '1 item in wishlist' : `${count} items in wishlist`;
        }

        if (count > 0) {
          bubble.style.display = 'flex';
          bubble.removeAttribute('hidden');
          bubble.setAttribute('aria-hidden', 'false');
        } else {
          bubble.style.display = 'none';
          bubble.setAttribute('hidden', '');
          bubble.setAttribute('aria-hidden', 'true');
        }
      });
    }

    queueSync() {
      if (!this.hasCustomer() || !this.hiddenForm || !this.hiddenInput) {
        return;
      }

      clearTimeout(this.syncTimer);
      this.syncTimer = window.setTimeout(() => this.syncMetafield(), SYNC_DELAY);
    }

    syncMetafield() {
      if (!this.hasCustomer() || !this.hiddenForm || !this.hiddenInput) {
        return;
      }

      const payload = JSON.stringify(this.items);
      if (payload === this.lastSyncedPayload) {
        return;
      }

      this.lastSyncedPayload = payload;
      this.hiddenInput.value = payload;

      try {
        const formData = new FormData(this.hiddenForm);
        const params = new URLSearchParams();
        formData.forEach((value, key) => {
          if (typeof value === 'string') {
            params.append(key, value);
          }
        });

        const method = (this.hiddenForm.getAttribute('method') || 'post').toUpperCase();
        const action =
          (this.hiddenForm && this.hiddenForm.getAttribute('action')) ||
          (window.routes && window.routes.account_url) ||
          '/account';

        fetch(action, {
          method,
          body: params.toString(),
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            Accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
          },
          credentials: 'same-origin',
          redirect: 'manual',
        }).catch((error) => {
          console.error('Wishlist: metafield sync failed', error);
        });
      } catch (error) {
        console.error('Wishlist: metafield sync error', error);
      }
    }
  }

  window.ShopifyWishlist = new WishlistManager();
})();
